import { useEffect, useMemo, useState } from "react";
import { UploadCloud, FileSpreadsheet, Download, ArrowRight, ArrowLeft, Loader2, AlertTriangle, Info } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useWorkspace } from "../../context/WorkspaceContext";
import { SearchableSelect } from "./SearchableSelect";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

/** Reusable Confirm-Context → Template → Upload → Preview → Apply wizard for
 *  project+type Excel operations (Backlog Completion, Bulk Delete). */
export function OpsWizard({ testid, needsType = true, templateUrl, previewUrl, applyUrl, rejectedUrlBase, statusUrlBase,
                            requireConfirm = false, confirmText = "", applyLabel, dangerTone = false, extraNote }) {
  const { ctx, options } = useWorkspace();
  const projects = useMemo(() => options.projects || [], [options.projects]);
  const [types, setTypes] = useState([]);
  const [projectId, setProjectId] = useState(ctx.project?.id || "");
  const [typeId, setTypeId] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [dlBusy, setDlBusy] = useState(false);
  const [catFilter, setCatFilter] = useState("__all__");
  const [ack, setAck] = useState(false);
  const [progress, setProgress] = useState(null);

  useEffect(() => { api.get("/material-types", { params: { active_only: true, page_size: 500 } }).then((r) => setTypes(r.data.items || [])); }, []);
  useEffect(() => { setProjectId(ctx.project?.id || ""); setConfirmed(false); }, [ctx.project?.id]);

  const projName = useMemo(() => projects.find((p) => p.id === projectId), [projects, projectId]);
  const typeName = useMemo(() => types.find((t) => t.id === typeId), [types, typeId]);
  const reset = () => { setFile(null); setPreview(null); setCatFilter("__all__"); setAck(false); };

  const download = async () => {
    setDlBusy(true);
    try {
      const res = await api.get(templateUrl, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "template.xlsx"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Template download failed"); } finally { setDlBusy(false); }
  };

  const doPreview = async () => {
    if (!projectId || (needsType && !typeId) || !file) return;
    setLoading(true); setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("project_id", projectId);
      if (needsType) fd.append("material_type_id", typeId);
      const { data } = await api.post(previewUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); } finally { setLoading(false); }
  };

  const pollStatus = async (batchId) => {
    // Large batches run in the background server-side; poll until a terminal state.
    for (let attempt = 0; attempt < 480; attempt++) {
      await new Promise((r) => setTimeout(r, 2500));
      let data;
      try { ({ data } = await api.get(`${statusUrlBase}/${batchId}`)); } catch { continue; }
      if (data.progress && (data.progress.total || data.progress.completed)) setProgress(data.progress);
      if (data.status === "applied") { toast.success("Applied successfully"); setPreview((p) => ({ ...p, applied: true, result: data.result || data })); return; }
      if (data.status === "apply_failed") { toast.error(data.error || "Apply failed"); return; }
    }
    toast.error("Apply is taking longer than expected — check the Material Register / try again");
  };

  const doApply = async () => {
    if (!preview?.batch_id) return;
    setApplying(true); setProgress(null);
    try {
      const body = requireConfirm ? { batch_id: preview.batch_id, confirm: true } : { batch_id: preview.batch_id };
      const { data } = await api.post(applyUrl, body);
      if (statusUrlBase && data.status === "applying") {
        setProgress(data.progress || null);
        await pollStatus(preview.batch_id);
      } else {
        toast.success("Applied successfully");
        setPreview((p) => ({ ...p, applied: true, result: data }));
      }
    } catch (e) {
      // If async status is available the request may have merely timed out while the
      // server keeps working — recover by polling instead of showing a false failure.
      if (statusUrlBase) { await pollStatus(preview.batch_id); }
      else { toast.error(e.response?.data?.detail || "Apply failed"); }
    } finally { setApplying(false); }
  };

  const downloadRejected = async () => {
    try {
      const res = await api.get(`${rejectedUrlBase}/${preview.batch_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "rejected_rows.xlsx"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const s = preview?.summary || {};
  const rows = useMemo(() => {
    if (!preview?.rows) return [];
    return catFilter === "__all__" ? preview.rows : preview.rows.filter((r) => r.category === catFilter);
  }, [preview, catFilter]);
  const applicable = s.applicable || 0;
  const rejected = s.rejected || 0;
  const canApply = preview && !preview.applied && applicable > 0 && (!requireConfirm || ack);

  return (
    <div data-testid={`${testid}-wizard`}>
      {!confirmed ? (
        <div className="max-w-2xl rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid={`${testid}-context-step`}>
          <p className="mb-4 text-xs text-slate-500">{extraNote || "Confirm the Project and Material Type before choosing a file."}</p>
          <div className="grid gap-4 md:grid-cols-2">
            <div><Label>Project *</Label>
              <SearchableSelect testid={`${testid}-project`} value={projectId} onChange={(v) => { setProjectId(v); setConfirmed(false); }} options={projects.map((p) => ({ value: p.id, label: p.project_code ? `${p.project_code} — ${p.project_name}` : p.project_name }))} placeholder="Select project" className="mt-1" /></div>
            {needsType && <div><Label>Material Type *</Label>
              <SearchableSelect testid={`${testid}-type`} value={typeId} onChange={(v) => { setTypeId(v); setConfirmed(false); }} options={types.map((t) => ({ value: t.id, label: t.type_name }))} placeholder="Select material type" className="mt-1" /></div>}
          </div>
          {projectId && (!needsType || typeId) && (
            <div className="mt-5 rounded-lg border border-blue-200 bg-blue-50 p-4" data-testid={`${testid}-context-summary`}>
              <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">Please confirm</div>
              <div className="mt-2 text-sm text-slate-700">Project: <span className="font-semibold">{projName?.project_name}</span>{needsType && <> · Type: <span className="font-semibold">{typeName?.type_name}</span></>}</div>
            </div>
          )}
          <div className="mt-5 flex justify-end">
            <Button data-testid={`${testid}-confirm-context`} disabled={!projectId || (needsType && !typeId)} onClick={() => setConfirmed(true)} className="bg-blue-600 hover:bg-blue-700">Confirm &amp; Continue <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5" data-testid={`${testid}-confirmed-bar`}>
            <div className="text-sm text-slate-700">Project: <span className="font-semibold text-blue-800">{projName?.project_name}</span>{needsType && <> · Type: <span className="font-semibold text-blue-800">{typeName?.type_name}</span></>}</div>
            <Button variant="outline" size="sm" data-testid={`${testid}-back-context`} onClick={() => { setConfirmed(false); reset(); }}><ArrowLeft className="mr-1.5 h-4 w-4" /> Change Project / Type</Button>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <span className="text-sm font-medium text-slate-600">Upload the filled template (.xlsx)</span>
              <Button variant="outline" size="sm" data-testid={`${testid}-template-download`} onClick={download} disabled={dlBusy}>{dlBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />} Download Template</Button>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <label className="flex flex-1 cursor-pointer items-center gap-3 rounded-lg border border-dashed border-slate-300 px-4 py-3 hover:bg-slate-50">
                <FileSpreadsheet className="h-5 w-5 text-blue-600" /><span className="text-sm text-slate-600">{file ? file.name : "Choose .xlsx file"}</span>
                <input type="file" accept=".xlsx,.xls" className="hidden" data-testid={`${testid}-file`} onChange={(e) => { setFile(e.target.files[0]); setPreview(null); }} />
              </label>
              <Button data-testid={`${testid}-preview`} onClick={doPreview} disabled={loading || !file} className="bg-slate-800 hover:bg-slate-900">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UploadCloud className="mr-1.5 h-4 w-4" /> Preview</>}</Button>
            </div>
          </div>

          {preview && (
            <div className="mt-6 space-y-4">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid={`${testid}-summary`}>
                {Object.entries(s).map(([k, v]) => (
                  <div key={k} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="text-xs font-medium uppercase tracking-wide text-slate-400">{k}</div>
                    <div className={`mt-1 font-display text-2xl font-bold ${k === "rejected" && v > 0 ? "text-red-600" : k === "applicable" ? "text-blue-600" : "text-slate-700"}`}>{v}</div>
                  </div>
                ))}
              </div>

              {rejected > 0 && (
                <div className="flex items-center justify-between rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid={`${testid}-partial-warning`}>
                  <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {rejected} row(s) rejected. Continue with {applicable} valid, or download & fix the rejected rows.</span>
                  <Button variant="outline" size="sm" data-testid={`${testid}-download-rejected`} onClick={downloadRejected}><Download className="mr-1.5 h-4 w-4" /> Rejected Rows</Button>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Select value={catFilter} onValueChange={setCatFilter}>
                  <SelectTrigger data-testid={`${testid}-cat-filter`} className="w-64"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="__all__">All categories</SelectItem>{Object.keys(preview.categories || {}).map((c) => <SelectItem key={c} value={c}>{c} ({preview.categories[c]})</SelectItem>)}</SelectContent>
                </Select>
                <div className="flex items-center gap-3">
                  {preview.applied ? <Badge className="bg-emerald-100 px-3 py-1.5 text-emerald-700" data-testid={`${testid}-applied`}>Done · {JSON.stringify(preview.result).slice(0, 60)}</Badge> : (
                    <>
                      {requireConfirm && applicable > 0 && (
                        <label className="flex items-center gap-2 text-sm text-red-700"><Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)} data-testid={`${testid}-ack`} /> {confirmText || "I confirm this action"}</label>
                      )}
                      <Button variant="outline" onClick={reset} data-testid={`${testid}-reset`}>Reset</Button>
                      <Button data-testid={`${testid}-apply`} onClick={doApply} disabled={!canApply || applying} className={dangerTone ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"}>{applying ? <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />{progress?.total ? `Completing ${progress.completed || 0}/${progress.total}…` : "Applying…"}</span> : (applyLabel ? applyLabel(applicable) : `Apply ${applicable}`)}</Button>
                    </>
                  )}
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                <table className="w-full text-left">
                  <thead><tr className="border-b bg-slate-100/80">
                    {["Row", "Serial", "Type", "Current Status", "Result"].map((h) => <th key={h} className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>)}
                  </tr></thead>
                  <tbody data-testid={`${testid}-rows`}>
                    {rows.slice(0, 500).map((r) => (
                      <tr key={r.row_no} className="border-b border-slate-100">
                        <td className="px-4 py-2 text-sm text-slate-500">{r.row_no}</td>
                        <td className="px-4 py-2 font-mono text-xs text-slate-800">{r.serial || "—"}</td>
                        <td className="px-4 py-2 text-sm text-slate-600">{r.material_type}</td>
                        <td className="px-4 py-2 text-xs">{r.current_status || "—"}{r.proposed_status && <span className="text-emerald-600"> → {r.proposed_status}</span>}</td>
                        <td className="px-4 py-2"><Badge className={`${r.severity === "ok" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"} hover:opacity-100`}>{r.category}</Badge>{r.message && <span className="ml-2 text-xs text-slate-400">{r.message}</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
